import Link from "next/link";
import { CSSObject } from "@emotion/react";
import React from "react";
import { NextRouter, useRouter } from "next/router";

import { ContentOutline } from "@/bundles/types/ContentOutline";
import {
  breadcrumbContainerCss,
  breadcrumbItemCss,
  itemSeparatorCss,
  collapsingEllipsisCss,
  xmallHideCss,
} from "./Breadcrumbs.styles";

const MAX_NUMBER_OF_MIDDLE_ITEMS = 2;

type BreadcrumbsProps = {
  contentOutline: ContentOutline;
  currentContentIdTree: string; // Format: "id1/id2/.../idN".
  makeHrefWithTree: (
    router: NextRouter,
    tree: string,
  ) => { pathname: string; query: { [key: string]: string } };
};

const applyMobileHideIfMiddle = (firstCss: CSSObject, isMiddle: boolean) => [
  firstCss,
  isMiddle ? xmallHideCss : undefined,
];

const collapsingEllipsis = (showToAnySize: boolean) => (
  <span css={collapsingEllipsisCss(showToAnySize)}>/ ...</span>
);

const itemSeparator = (isMiddle: boolean) => (
  <span css={applyMobileHideIfMiddle(itemSeparatorCss, isMiddle)}>/</span>
);

/**
 * Breadcrumbs that collapses and truncates items when necessary.
 *
 * Features:
 *
 * - The root ID of `currentContentIdTree` is always ignored.
 * - It never shows a single item breadcrumb.
 * - The first and the last items are always visible, but they might be
 *   truncated.
 * - Only two items for mobile screens.
 * - Maximum of 4 items.
 * - Only the first middle items can be collapsed.
 */
const Breadcrumbs = ({
  contentOutline,
  currentContentIdTree,
  makeHrefWithTree,
}: BreadcrumbsProps) => {
  const router = useRouter();
  if (!currentContentIdTree.includes("/")) return null;

  const outlineItems = contentOutline?.items ?? {};
  const treeArray = currentContentIdTree.split("/");

  // We don't need to show breadcrumbs with a single item (we should always
  // ignore the root ID).
  if (treeArray.length <= 2) return null;

  // Example: [first, middle, last] -> length 3 + 1 (root)
  const hasMiddleItems = treeArray.length > 3;
  // We need to hide the first middle items if the number of middle breadcrumb
  // parts is bigger then MAX_NUMBER_OF_MIDDLE_ITEMS. This variables counts how
  // many middle items we still need to hide.
  let counterOfMiddleItemsToHide =
    treeArray.length - 3 - MAX_NUMBER_OF_MIDDLE_ITEMS;

  return (
    <nav aria-label="Breadcrumb" css={breadcrumbContainerCss}>
      {treeArray
        // We drop the first item because it always represents the root content
        // (such as STEM), which we don't want to include in the breadcrumbs.
        .slice(1)
        .map((_, idx) => {
          // For each breadcrumb item, we find its name by building a partial
          // tree route (up to its ID) and getting its outline object from
          // `outlineItems`.
          // `idxNoSlice` is the index value we would have if we had `slice`
          // after `map`. `slice` before `map` is just an optimization.
          const idxNoSlice = idx + 1;
          const partialTree: string = treeArray
            .slice(0, idxNoSlice + 1)
            .join("/");
          const breadcrumbItem = outlineItems[partialTree];
          if (!breadcrumbItem) return null;

          const isLast = idxNoSlice === treeArray.length - 1;
          const isFirst = idxNoSlice === 1;
          const isSecond = idxNoSlice === 2;
          const isMiddle = !isFirst && !isLast;
          const mustHideMiddle = counterOfMiddleItemsToHide > 0;
          if (isMiddle) --counterOfMiddleItemsToHide;

          return (
            <React.Fragment key={breadcrumbItem.object_id}>
              {/* Separator. We never show it before the first item, and we may
              need hide it for small screens. */}
              {!isFirst && !mustHideMiddle && itemSeparator(isMiddle)}
              {/* Ellipsis for small screens. We also show it if the number of
              middle items exceed the maximum. */}
              {hasMiddleItems && isSecond && collapsingEllipsis(mustHideMiddle)}
              {/* Breadcrumb items. The first and the last must always be
              visible. */}
              {(!isMiddle || !mustHideMiddle) &&
                // The last breadcrumb item should never be a link.
                (isLast ? (
                  <span
                    css={breadcrumbItemCss(isLast)}
                    title={breadcrumbItem.name}
                  >
                    {breadcrumbItem.name}
                  </span>
                ) : (
                  <Link
                    href={makeHrefWithTree(router, partialTree)}
                    css={applyMobileHideIfMiddle(
                      breadcrumbItemCss(isLast),
                      isMiddle,
                    )}
                    title={breadcrumbItem.name}
                    data-qa-selector="bundle-breadcrumb"
                    shallow
                  >
                    {breadcrumbItem.name}
                  </Link>
                ))}
            </React.Fragment>
          );
        })}
    </nav>
  );
};

export default Breadcrumbs;
